/**
 * Language availability — port of LanguageStore.refreshAvailability: locales with a
 * published localization for every playable (catalog ∩ visible) episode, `en` always,
 * cached in localStorage (`teacheck.language.available-languages`) for offline launches.
 */
import { EPISODE_CATALOG } from '@/domain';
import { AVAILABLE_KEY } from '@/services/i18n';

export function readCachedAvailableLocales(): Set<string> {
  try {
    const raw = localStorage.getItem(AVAILABLE_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(['en', ...(Array.isArray(list) ? list.filter((v): v is string => typeof v === 'string') : [])]);
  } catch {
    return new Set(['en']);
  }
}

export function writeCachedAvailableLocales(locales: ReadonlySet<string>): void {
  try { localStorage.setItem(AVAILABLE_KEY, JSON.stringify([...locales].sort())); } catch { /* ignore */ }
}

export function playableEpisodeIDs(visible: ReadonlySet<string>): string[] {
  const ids = EPISODE_CATALOG.map((e) => e.id).filter((id) => visible.has(id));
  return ids.length > 0 ? ids : EPISODE_CATALOG.map((e) => e.id);
}

export async function refreshAvailableLocales(deps: {
  visibleEpisodeIDs(): Promise<Set<string>>;
  availableLocalizationLocales(ids: string[], options?: { throwOnError?: boolean }): Promise<Set<string>>;
}): Promise<{ visible: Set<string>; available: Set<string> }> {
  const visible = await deps.visibleEpisodeIDs();
  const available = await deps.availableLocalizationLocales(playableEpisodeIDs(visible), { throwOnError: true });
  available.add('en');
  writeCachedAvailableLocales(available);
  return { visible, available };
}
